import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState, useEffect } from 'react';
import {
    Wallet,
    CreditCard,
    DollarSign,
    ArrowUpRight,
    ArrowDownLeft,
    Eye,
    Filter,
    Search,
    Download,
    Plus,
    RefreshCw,
    AlertCircle,
    CheckCircle,
    Clock,
    Users,
    Building2,
    Calendar as CalendarIcon
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Table } from '../../components/ui/Table';
import { useGetWalletTransactionsQuery, useApproveRejectWalletTransactionMutation, useAddFundsMutation, useGetWalletStatsQuery } from '../../store/slices/apiSlice';

interface Transaction {
    transaction_id: number;
    user: {
        user_id: number;
        name: string;
        email: string;
        currency_symbol?: string;
    };
    amount: number;
    transaction_type: 'deposit' | 'withdrawal';
    status: 'pending' | 'completed' | 'failed' | 'rejected';
    payment_method: string;
    payment_screenshot: string | null;
    transaction_reference: string;
    description: string;
    balance_before: number;
    balance_after: number;
    verified_by: number | null;
    verified_at: string | null;
    rejection_reason: string | null;
    created_at: string;
    updated_at: string;
}

interface WalletStats {
    totalBalance: number;
    totalCredit: number;
    totalDebit: number;
    pendingTransactions: number;
    completedTransactions: number;
    failedTransactions: number;
}

const AdminWallet: React.FC = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [dateFilter, setDateFilter] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(10);
    const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [showAddFundsModal, setShowAddFundsModal] = useState(false);
    const [addFundsData, setAddFundsData] = useState({
        user_id: '',
        amount: '',
        description: ''
    });
    const [showTransactionDetailsModal, setShowTransactionDetailsModal] = useState(false);
    const [selectedTransactionForView, setSelectedTransactionForView] = useState<Transaction | null>(null);
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState<string>('');
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successData, setSuccessData] = useState<{
        amount_added: number;
        new_balance: number;
        transaction_id: number;
    } | null>(null);

    // API queries
    const { data: transactionsData, isLoading, error, refetch } = useGetWalletTransactionsQuery({
        page: currentPage,
        limit,
        status: statusFilter as any,
        transaction_type: typeFilter as any,
    });

    const { data: statsData, isLoading: statsLoading, error: statsError } = useGetWalletStatsQuery();

    // Approve/Reject mutation
    const [approveRejectTransaction, { isLoading: isProcessing }] = useApproveRejectWalletTransactionMutation();

    // Add funds mutation
    const [addFunds, { isLoading: isAddingFunds }] = useAddFundsMutation();

    const transactions = transactionsData?.data?.transactions || [];
    const pagination = transactionsData?.data?.pagination;
    const walletStats = statsData?.data;

    useEffect(() => {
        setIsVisible(true);
    }, []);

    const filteredTransactions = transactions.filter(transaction => {
        const matchesSearch = (transaction.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (transaction.user?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (transaction.user?.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (transaction.transaction_reference || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || transaction.status === statusFilter;
        const matchesType = typeFilter === 'all' || transaction.transaction_type === typeFilter;

        // Date Filtering Logic
        let matchesDate = true;
        if (dateFilter !== 'all') {
            const transactionDate = new Date(transaction.created_at);
            const now = new Date();
            
            if (dateFilter === 'today') {
                matchesDate = transactionDate.toDateString() === now.toDateString();
            } else if (dateFilter === 'week') {
                const oneWeekAgo = new Date();
                oneWeekAgo.setDate(now.getDate() - 7);
                matchesDate = transactionDate >= oneWeekAgo;
            } else if (dateFilter === 'month') {
                const oneMonthAgo = new Date();
                oneMonthAgo.setMonth(now.getMonth() - 1);
                matchesDate = transactionDate >= oneMonthAgo;
            } else if (dateFilter === 'custom') {
                if (startDate && endDate) {
                    const start = new Date(startDate);
                    const end = new Date(endDate);
                    end.setHours(23, 59, 59, 999);
                    matchesDate = transactionDate >= start && transactionDate <= end;
                }
            }
        }

        return matchesSearch && matchesStatus && matchesType && matchesDate;
    });

    const formatCurrency = (amount: number | string | null | undefined, currencySymbol: string = '₦') => {
        const value = parseFloat(String(amount || '0')) || 0;
        return `${currencySymbol}${new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value)}`;
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'completed':
                return <CheckCircle className="w-4 h-4 text-green-500" />;
            case 'pending':
                return <Clock className="w-4 h-4 text-yellow-500" />;
            case 'failed':
            case 'rejected':
                return <AlertCircle className="w-4 h-4 text-red-500" />;
            default:
                return <Clock className="w-4 h-4 text-gray-500" />;
        }
    };

    const getStatusBadge = (status: string) => {
        const variants = {
            completed: 'bg-green-100 text-green-700',
            pending: 'bg-yellow-100 text-yellow-700',
            failed: 'bg-red-100 text-red-700',
            rejected: 'bg-red-100 text-red-700'
        };

        return (
            <Badge className={variants[status as keyof typeof variants]}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </Badge>
        );
    };

    const getTypeIcon = (type: string) => {
        return type === 'deposit' ?
            <ArrowDownLeft className="w-4 h-4 text-green-500" /> :
            <ArrowUpRight className="w-4 h-4 text-red-500" />;
    };

    const handleApprove = async () => {
        if (!selectedTransaction) return;

        try {
            await approveRejectTransaction({
                transaction_id: selectedTransaction.transaction_id,
                action: 'approve'
            }).unwrap();

            setShowApproveModal(false);
            setSelectedTransaction(null);
            // The query will automatically refetch due to invalidatesTags
        } catch (error) {
            console.error('Failed to approve transaction:', error);
        }
    };

    const handleReject = async () => {
        if (!selectedTransaction) return;

        try {
            await approveRejectTransaction({
                transaction_id: selectedTransaction.transaction_id,
                action: 'reject',
                rejection_reason: rejectionReason.trim() || 'No reason provided'
            }).unwrap();

            setShowRejectModal(false);
            setSelectedTransaction(null);
            setRejectionReason('');
            // The query will automatically refetch due to invalidatesTags
        } catch (error) {
            console.error('Failed to reject transaction:', error);
        }
    };

    const openApproveModal = (transaction: Transaction) => {
        setSelectedTransaction(transaction);
        setShowApproveModal(true);
    };

    const openRejectModal = (transaction: Transaction) => {
        setSelectedTransaction(transaction);
        setShowRejectModal(true);
    };

    const handleAddFunds = async () => {
        if (!addFundsData.user_id || !addFundsData.amount || !addFundsData.description) {
            return;
        }

        try {
            const result = await addFunds({
                user_id: parseInt(addFundsData.user_id),
                amount: parseFloat(addFundsData.amount),
                description: addFundsData.description
            }).unwrap();

            // Show success modal with transaction details
            setSuccessData({
                amount_added: result.data.amount_added,
                new_balance: result.data.new_balance,
                transaction_id: result.data.transaction_id
            });
            setShowSuccessModal(true);

            setShowAddFundsModal(false);
            setAddFundsData({ user_id: '', amount: '', description: '' });
            // The query will automatically refetch due to invalidatesTags
        } catch (error) {
            console.error('Failed to add funds:', error);
            alert('Failed to add funds. Please try again.');
        }
    };

    const openTransactionDetails = (transaction: Transaction) => {
        setSelectedTransactionForView(transaction);
        setShowTransactionDetailsModal(true);
    };

    const openImageModal = (imageUrl: string) => {
        setSelectedImageUrl(imageUrl);
        setShowImageModal(true);
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Wallet Management</h1>
                        <p className="text-gray-600 mt-1">Monitor transactions and manage platform finances</p>
                    </div>
                </div>
            </div>

            <div className="p-6">
                {/* Stats Cards */}
                <div className={`mb-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    <Card className="p-6 bg-white border-none shadow-xl shadow-gray-100 max-w-xs group hover:scale-[1.02] transition-all duration-300">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-1">Pending Requests</p>
                                <p className="text-3xl font-black text-amber-500">
                                    {statsLoading ? '...' : walletStats ? walletStats.pending_transactions : 0}
                                </p>
                            </div>
                            <div className="p-4 bg-amber-50 rounded-2xl group-hover:bg-amber-100 transition-colors duration-500">
                                <Clock className="w-8 h-8 text-amber-500" />
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Premium Filtering Hub */}
                <Card className="p-6 mb-8 bg-white border-none shadow-xl rounded-2xl overflow-visible">
                    <div className="flex flex-col space-y-6">
                        <div className="flex flex-col lg:flex-row gap-4 items-center">
                            <div className="flex-1 w-full">
                                <div className="relative group">
                                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors w-5 h-5" />
                                    <Input
                                        placeholder="Search by description, user, or reference..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="pl-12 h-12 bg-gray-50 border-gray-100 focus:bg-white transition-all rounded-xl shadow-inner"
                                    />
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-4 w-full lg:w-auto">
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="flex-1 lg:min-w-[160px] h-12 px-4 bg-gray-50 border border-gray-100 rounded-xl text-sm font-bold text-gray-700 focus:ring-2 focus:ring-blue-500 outline-none transition-all cursor-pointer hover:bg-gray-100"
                                >
                                    <option value="all">All Status</option>
                                    <option value="completed">Completed</option>
                                    <option value="pending">Pending</option>
                                    <option value="rejected">Rejected</option>
                                </select>
                                <select
                                    value={typeFilter}
                                    onChange={(e) => setTypeFilter(e.target.value)}
                                    className="flex-1 lg:min-w-[160px] h-12 px-4 bg-gray-50 border border-gray-100 rounded-xl text-sm font-bold text-gray-700 focus:ring-2 focus:ring-blue-500 outline-none transition-all cursor-pointer hover:bg-gray-100"
                                >
                                    <option value="all">All Types</option>
                                    <option value="deposit">Deposit</option>
                                    <option value="withdrawal">Withdrawal</option>
                                </select>
                            </div>
                        </div>

                        {/* Date Filters Row */}
                        <div className="flex flex-col md:flex-row items-start md:items-center gap-6 pt-4 border-t border-gray-50">
                            <div className="flex flex-wrap gap-2">
                                {[
                                    { id: 'all', label: 'All Time' },
                                    { id: 'today', label: 'Today' },
                                    { id: 'week', label: 'This Week' },
                                    { id: 'month', label: 'This Month' },
                                    { id: 'custom', label: 'Custom Range' }
                                ].map((period) => (
                                    <button
                                        key={period.id}
                                        onClick={() => setDateFilter(period.id)}
                                        className={`px-5 py-2.5 rounded-full text-xs font-bold transition-all duration-300 ${
                                            dateFilter === period.id
                                                ? 'bg-blue-600 text-white shadow-lg shadow-blue-200'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                    >
                                        {period.label}
                                    </button>
                                ))}
                            </div>

                            {dateFilter === 'custom' && (
                                <div className="flex items-center gap-3 animate-in fade-in slide-in-from-left-4 duration-300">
                                    <div className="relative">
                                        <input
                                            type="date"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                            className="h-10 px-4 bg-gray-50 border border-gray-200 rounded-lg text-xs font-bold text-gray-700 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                        />
                                        <div className="absolute -top-5 left-1 text-[9px] font-black text-gray-400 uppercase tracking-tighter">Start Date</div>
                                    </div>
                                    <span className="text-gray-300 font-bold">/</span>
                                    <div className="relative">
                                        <input
                                            type="date"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                            className="h-10 px-4 bg-gray-50 border border-gray-200 rounded-lg text-xs font-bold text-gray-700 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                        />
                                        <div className="absolute -top-5 left-1 text-[9px] font-black text-gray-400 uppercase tracking-tighter">End Date</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </Card>

                {/* Transactions Table */}
                <Card className="overflow-hidden">
                    <div className="p-6 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">Recent Transactions</h3>
                        <p className="text-gray-600 mt-1">All wallet transactions and their status</p>
                    </div>
                    {isLoading ? (
                        <div className="text-center py-12">
                            <RefreshCw className="w-8 h-8 text-gray-400 mx-auto mb-4 animate-spin" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Loading transactions...</h3>
                            <p className="text-gray-500">Please wait while we fetch your data</p>
                        </div>
                    ) : error ? (
                        <div className="text-center py-12">
                            <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Error loading transactions</h3>
                            <p className="text-gray-500 mb-4">There was a problem fetching your transaction data</p>
                            <Button onClick={() => refetch()} className="flex items-center gap-2 mx-auto">
                                <RefreshCw className="w-4 h-4" />
                                Try Again
                            </Button>
                        </div>
                    ) : (
                        <Table
                            data={filteredTransactions}
                            itemsPerPage={limit}
                            columns={[
                                {
                                    key: 'sno',
                                    label: 'S.No',
                                    render: (_: any, __: any, index: number) => (
                                        <span className="font-bold text-gray-400 text-xs">#{index + 1}</span>
                                    ),
                                },
                                {
                                    key: 'transaction_type',
                                    label: 'Transaction',
                                    render: (value: string, row: Transaction) => (
                                        <div className="flex items-center">
                                            {getTypeIcon(value)}
                                            <div className="ml-3">
                                                <div className="text-sm font-medium text-gray-900 capitalize">
                                                    {value}
                                                </div>
                                                <div className="text-xs text-gray-500 font-mono">
                                                    {row.transaction_reference}
                                                </div>
                                            </div>
                                        </div>
                                    ),
                                },
                                {
                                    key: 'amount',
                                    label: 'Amount',
                                    render: (value: number, row: Transaction) => (
                                        <div className={`text-sm font-bold ${row.transaction_type === 'deposit' ? 'text-green-600' : 'text-red-600'}`}>
                                            {row.transaction_type === 'deposit' ? '+' : '-'}{formatCurrency(value, row.user.currency_symbol)}
                                        </div>
                                    ),
                                },
                                {
                                    key: 'user',
                                    label: 'User/Entity',
                                    render: (user: Transaction['user']) => (
                                        <div>
                                            <div className="text-sm font-bold text-gray-900">{user.name}</div>
                                            <div className="text-xs text-gray-500">{user.email}</div>
                                        </div>
                                    ),
                                },
                                {
                                    key: 'payment_method',
                                    label: 'Method',
                                    render: (value: string) => <span className="text-sm font-medium text-gray-600">{value || '-'}</span>
                                },
                                {
                                    key: 'payment_screenshot',
                                    label: 'Screenshot',
                                    render: (value: string | null) => value ? (
                                        <button
                                            onClick={() => openImageModal(value)}
                                            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors group"
                                        >
                                            <Eye className="w-4 h-4 group-hover:scale-110 transition-transform" />
                                            <span className="text-xs font-bold underline decoration-blue-200 underline-offset-4">View</span>
                                        </button>
                                    ) : (
                                        <span className="text-xs text-gray-400 italic">No proof</span>
                                    ),
                                },
                                {
                                    key: 'status',
                                    label: 'Status',
                                    render: (value: string) => (
                                        <div className="flex items-center gap-2">
                                            {getStatusIcon(value)}
                                            {getStatusBadge(value)}
                                        </div>
                                    ),
                                },
                                {
                                    key: 'created_at',
                                    label: 'Date',
                                    render: (value: string) => (
                                        <div className="flex flex-col">
                                            <span className="text-xs font-bold text-gray-900">{formatDateUtil(value)}</span>
                                            <span className="text-[10px] text-gray-400">{new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                        </div>
                                    ),
                                },
                                {
                                    key: 'actions',
                                    label: 'Actions',
                                    render: (_: any, row: Transaction) => (
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="h-8 px-3 border-gray-200 hover:bg-gray-50"
                                                onClick={() => openTransactionDetails(row)}
                                            >
                                                <Eye className="w-3 h-3 mr-1" /> View
                                            </Button>
                                            {row.status === 'pending' && (
                                                <div className="flex gap-1">
                                                    <Button
                                                        size="sm"
                                                        className="h-8 px-3 bg-green-600 hover:bg-green-700 text-white border-none shadow-sm"
                                                        onClick={() => openApproveModal(row)}
                                                        disabled={isProcessing}
                                                    >
                                                        <CheckCircle className="w-3 h-3 mr-1" /> Approve
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        className="h-8 px-3 bg-red-600 hover:bg-red-700 text-white border-none shadow-sm"
                                                        onClick={() => openRejectModal(row)}
                                                        disabled={isProcessing}
                                                    >
                                                        <AlertCircle className="w-3 h-3 mr-1" /> Reject
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    ),
                                },
                            ]}
                        />
                    )}

                    {!isLoading && !error && filteredTransactions.length === 0 && (
                        <div className="text-center py-12">
                            <Wallet className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No transactions found</h3>
                            <p className="text-gray-500">Try adjusting your search or filter criteria</p>
                        </div>
                    )}

                </Card>
            </div>

            {/* Approve Confirmation Modal */}
            {showApproveModal && selectedTransaction && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-green-100 rounded-full">
                                <CheckCircle className="w-6 h-6 text-green-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">Approve Transaction</h3>
                        </div>
                        <p className="text-gray-600 mb-6">
                            Are you sure you want to approve this transaction?
                        </p>
                        <div className="bg-gray-50 p-4 rounded-lg mb-6">
                            <div className="text-sm text-gray-600">
                                <div><strong>Amount:</strong> {formatCurrency(selectedTransaction.amount, selectedTransaction.user.currency_symbol)}</div>
                                <div><strong>User:</strong> {selectedTransaction.user.name}</div>
                                <div><strong>Reference:</strong> {selectedTransaction.transaction_reference}</div>
                            </div>
                        </div>
                        <div className="flex gap-3 justify-end">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowApproveModal(false);
                                    setSelectedTransaction(null);
                                }}
                                disabled={isProcessing}
                            >
                                Cancel
                            </Button>
                            <Button
                                className="bg-green-600 hover:bg-green-700 text-white"
                                onClick={handleApprove}
                                disabled={isProcessing}
                            >
                                {isProcessing ? 'Processing...' : 'Approve Transaction'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reject Confirmation Modal */}
            {showRejectModal && selectedTransaction && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-red-100 rounded-full">
                                <AlertCircle className="w-6 h-6 text-red-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">Reject Transaction</h3>
                        </div>
                        <p className="text-gray-600 mb-4">
                            Are you sure you want to reject this transaction?
                        </p>
                        <div className="bg-gray-50 p-4 rounded-lg mb-4">
                            <div className="text-sm text-gray-600">
                                <div><strong>Amount:</strong> {formatCurrency(selectedTransaction.amount, selectedTransaction.user.currency_symbol)}</div>
                                <div><strong>User:</strong> {selectedTransaction.user.name}</div>
                                <div><strong>Reference:</strong> {selectedTransaction.transaction_reference}</div>
                            </div>
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Rejection Reason (Optional)
                            </label>
                            <textarea
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                rows={3}
                                placeholder="Enter reason for rejection (optional)..."
                            />
                        </div>
                        <div className="flex gap-3 justify-end">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowRejectModal(false);
                                    setSelectedTransaction(null);
                                    setRejectionReason('');
                                }}
                                disabled={isProcessing}
                            >
                                Cancel
                            </Button>
                            <Button
                                className="bg-red-600 hover:bg-red-700 text-white"
                                onClick={handleReject}
                                disabled={isProcessing}
                            >
                                {isProcessing ? 'Processing...' : 'Reject Transaction'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Funds Modal */}
            {showAddFundsModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-green-100 rounded-full">
                                <Plus className="w-6 h-6 text-green-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">Add Funds to User Wallet</h3>
                        </div>
                        <p className="text-gray-600 mb-6">
                            Add funds to a user's wallet account.
                        </p>

                        <div className="space-y-4 mb-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    User ID
                                </label>
                                <Input
                                    type="number"
                                    value={addFundsData.user_id}
                                    onChange={(e) => setAddFundsData({ ...addFundsData, user_id: e.target.value })}
                                    placeholder="Enter user ID"
                                    className="w-full"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Amount
                                </label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={addFundsData.amount}
                                    onChange={(e) => setAddFundsData({ ...addFundsData, amount: e.target.value })}
                                    placeholder="Enter amount"
                                    className="w-full"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description
                                </label>
                                <textarea
                                    value={addFundsData.description}
                                    onChange={(e) => setAddFundsData({ ...addFundsData, description: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                    rows={3}
                                    placeholder="Enter description for this transaction..."
                                />
                            </div>
                        </div>

                        <div className="flex gap-3 justify-end">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowAddFundsModal(false);
                                    setAddFundsData({ user_id: '', amount: '', description: '' });
                                }}
                                disabled={isAddingFunds}
                            >
                                Cancel
                            </Button>
                            <Button
                                className="bg-green-600 hover:bg-green-700 text-white"
                                onClick={handleAddFunds}
                                disabled={isAddingFunds || !addFundsData.user_id || !addFundsData.amount || !addFundsData.description}
                            >
                                {isAddingFunds ? 'Adding Funds...' : 'Add Funds'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Transaction Details Modal */}
            {showTransactionDetailsModal && selectedTransactionForView && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-2 bg-blue-100 rounded-full">
                                <Eye className="w-6 h-6 text-blue-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">Transaction Details</h3>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            {/* Transaction Info */}
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Transaction ID</label>
                                    <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{selectedTransactionForView.transaction_id}</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
                                    <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{selectedTransactionForView.transaction_reference}</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                                    <div className="flex items-center gap-2">
                                        {getTypeIcon(selectedTransactionForView.transaction_type)}
                                        <span className="text-sm text-gray-900 capitalize">{selectedTransactionForView.transaction_type}</span>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                                    <p className={`text-lg font-semibold ${selectedTransactionForView.transaction_type === 'deposit' ? 'text-green-600' : 'text-red-600'}`}>
                                        {selectedTransactionForView.transaction_type === 'deposit' ? '+' : '-'}{formatCurrency(selectedTransactionForView.amount, selectedTransactionForView.user.currency_symbol)}
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                    <div className="flex items-center gap-2">
                                        {getStatusIcon(selectedTransactionForView.status)}
                                        {getStatusBadge(selectedTransactionForView.status)}
                                    </div>
                                </div>
                            </div>

                            {/* User Info */}
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">User Name</label>
                                    <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{selectedTransactionForView.user.name}</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">User Email</label>
                                    <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{selectedTransactionForView.user.email}</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">User ID</label>
                                    <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{selectedTransactionForView.user.user_id}</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                                    <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{selectedTransactionForView.payment_method || 'N/A'}</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Created Date</label>
                                    <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                                        {formatDateTimeUtil(selectedTransactionForView.created_at)}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Description */}
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                            <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded">{selectedTransactionForView.description}</p>
                        </div>

                        {/* Balance Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Balance Before</label>
                                <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{formatCurrency(selectedTransactionForView.balance_before, selectedTransactionForView.user.currency_symbol)}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Balance After</label>
                                <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{formatCurrency(selectedTransactionForView.balance_after, selectedTransactionForView.user.currency_symbol)}</p>
                            </div>
                        </div>

                        {/* Additional Info */}
                        {(selectedTransactionForView.verified_by || selectedTransactionForView.verified_at || selectedTransactionForView.rejection_reason) && (
                            <div className="border-t pt-4 mb-6">
                                <h4 className="text-sm font-medium text-gray-700 mb-3">Additional Information</h4>
                                <div className="space-y-2">
                                    {selectedTransactionForView.verified_by && (
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">Verified By</label>
                                            <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">Admin ID: {selectedTransactionForView.verified_by}</p>
                                        </div>
                                    )}
                                    {selectedTransactionForView.verified_at && (
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">Verified At</label>
                                            <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                                                {formatDateTimeUtil(selectedTransactionForView.verified_at)}
                                            </p>
                                        </div>
                                    )}
                                    {selectedTransactionForView.rejection_reason && (
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500 mb-1">Rejection Reason</label>
                                            <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">{selectedTransactionForView.rejection_reason}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="flex justify-end">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowTransactionDetailsModal(false);
                                    setSelectedTransactionForView(null);
                                }}
                            >
                                Close
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Image Modal */}
            {showImageModal && selectedImageUrl && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Payment Screenshot</h3>
                            <button
                                onClick={() => {
                                    setShowImageModal(false);
                                    setSelectedImageUrl('');
                                }}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="text-center">
                            <img
                                src={selectedImageUrl}
                                alt="Payment Screenshot"
                                className="max-w-full max-h-[70vh] mx-auto rounded-lg shadow-lg"
                                onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xNzUgMTI1SDIyNVYxNzVIMTc1VjEyNVoiIGZpbGw9IiM5Q0EzQUYiLz4KPHBhdGggZD0iTTE5NSAxNDVIMjA1VjE1NUgxOTVWMTQ1WiIgZmlsbD0iIzlDQTNBRiIvPgo8dGV4dCB4PSIyMDAiIHk9IjE5MCIgZmlsbD0iIzlDQTNBRiIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE0Ij5JbWFnZSBub3QgZm91bmQ8L3RleHQ+Cjwvc3ZnPg==';
                                }}
                            />
                        </div>

                        <div className="mt-4 text-center">
                            <a
                                href={selectedImageUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                                Open in new tab
                            </a>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Modal */}
            {showSuccessModal && successData && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-green-100 rounded-full">
                                <CheckCircle className="w-6 h-6 text-green-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">Funds Added Successfully!</h3>
                        </div>

                        <div className="bg-green-50 p-4 rounded-lg mb-6">
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Amount Added:</span>
                                    <span className="font-semibold text-green-600">{formatCurrency(successData.amount_added)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">New Balance:</span>
                                    <span className="font-semibold text-gray-900">{formatCurrency(successData.new_balance)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Transaction ID:</span>
                                    <span className="font-mono text-gray-900">#{successData.transaction_id}</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end">
                            <Button
                                className="bg-green-600 hover:bg-green-700 text-white"
                                onClick={() => {
                                    setShowSuccessModal(false);
                                    setSuccessData(null);
                                }}
                            >
                                Close
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminWallet;
